import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Home, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * Breadcrumbs Component with JSON-LD structured data
 */
export default function Breadcrumbs({ items = [], municipio = null, className = '' }) {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  
  const currentMunicipio = municipio || (municipioSlug ? { slug: municipioSlug } : null);
  
  const breadcrumbItems = [];
  
  breadcrumbItems.push({
    label: 'Início',
    path: currentMunicipio?.slug ? `/${currentMunicipio.slug}` : '/',
    icon: Home
  });
  
  if (currentMunicipio?.nome) {
    breadcrumbItems.push({
      label: currentMunicipio.nome,
      path: `/${currentMunicipio.slug}`,
      icon: MapPin,
      isMunicipio: true
    });
  }
  
  items.forEach(item => {
    let adjustedPath = item.path;
    if (adjustedPath && currentMunicipio?.slug && !adjustedPath.startsWith(`/${currentMunicipio.slug}`)) {
      adjustedPath = `/${currentMunicipio.slug}${adjustedPath}`;
    }
    
    breadcrumbItems.push({
      ...item,
      path: adjustedPath
    });
  });

  // Build JSON-LD BreadcrumbList
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.path ? { item: `${window.location.origin}${item.path}` } : {}),
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <nav className={`flex items-center gap-1 text-sm ${className}`} aria-label="Breadcrumb">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const Icon = item.icon;
          
          return (
            <React.Fragment key={index}>
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-white/40 flex-shrink-0" />
              )}
              
              {isLast || !item.path ? (
                <span className="flex items-center gap-1 text-white font-medium truncate max-w-[200px]">
                  {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="truncate">{item.label}</span>
                </span>
              ) : (
                <button
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1 text-white/70 hover:text-white transition-colors truncate max-w-[150px] ${
                    item.isMunicipio ? 'bg-white/10 px-2 py-0.5 rounded-full' : ''
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="truncate">{item.label}</span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
}
